/** What every part of their page reads and does, handed down once from TheirPage. */
import { createContext, useContext } from "react";
import type { LiveNoteUpload } from "../../lib/liveNoteUpload";
import type { GuestCircleResponse, GuestPrefs, GuestTalkResponse } from "../../../shared/talk";
import type { WatchAnswer, WatchPageResponse, WatchPin, WatchSession } from "../../../shared/types";
import type { Conversation, Entry, FileItem, Host } from "./model";
import type { Place } from "./talk";
import type { ReplyTarget } from "./thread";

/** The sheets that open over the page, newest last. `room` is a room's sessions; `tell` is "Tell me when". */
export type SheetKey = "talk" | "room" | "answers" | "tell" | "pins" | "photo" | "snap" | "rec" | "call";
/** The four readings of a screen, by width: a phone, an iPad upright, an iPad on its side, a computer. */
export type Lay = "phone" | "upright" | "side" | "computer";

/** A circle's next call, as the sheet that opens from its line says it and takes "I am coming". */
export interface CallSheetData {
  circleId: string;
  name: string;
  next: NonNullable<GuestCircleResponse["nextCall"]>;
}

/** What the composer is replying to: a chat message (its thread), an update (its replies) or an announcement (an answer to the host). */
export type Replying =
  | { kind: "message"; target: ReplyTarget; entryKey: string; webinar_id: string }
  | { kind: "post"; postId: string; entryKey: string; webinar_id: string }
  | { kind: "announcement"; post: { id: string; body: string }; webinar_id: string };

/** A file waiting in the composer. */
export interface Chip {
  id: string;
  file: File;
  name: string;
  size: string;
  kind: "image" | "video" | "file";
  ext: string;
  label: string;
  preview: string | null;
}

/** One floating menu: the composer's + or a calendar's choices. */
export interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  sub?: string;
  run: () => void;
}
export interface MenuState {
  anchor: DOMRect;
  items: MenuItem[];
  attach?: boolean;
}

/** A picture or video opened full screen from the conversation. */
export interface Viewing {
  url: string;
  name: string;
  meta: string;
  video: boolean;
}

export interface PageValue {
  token: string;
  data: WatchPageResponse;
  /** The left side, What's new and the places: read with the page and again when a place changes. */
  guest: GuestTalkResponse;
  host: Host;
  me: { name: string; first: string; email: string; avatar: string | null };
  online: boolean;
  nowMs: number;
  convo: Conversation;
  pins: WatchPin[];
  files: FileItem[];
  answers: WatchAnswer[];
  /** The recorded talk the person is partway through. */
  resume: WatchSession | null;
  typing: boolean;
  lay: Lay;
  phone: boolean;

  // --- places ---
  place: Place;
  goPlace: (p: Place, replace?: boolean) => void;
  /** A phone's round Back: to its home. */
  back: () => void;
  /** The person has this place open: it is marked seen, and the counts are read again. */
  seen: (p: Place) => void;
  /** Calls `seen` for every place, and moves to the host. */
  markAllSeen: () => void;
  setPrefs: (p: Partial<GuestPrefs>) => void;
  /** The room whose sessions the sheet shows. */
  roomOpen: string | null;
  openRoom: (webinarId: string) => void;
  /** The circle call the call sheet shows. */
  call: CallSheetData | null;
  openCall: (c: CallSheetData) => void;

  // --- ui state ---
  pinsAll: boolean;
  togglePinsAll: () => void;
  openThreads: ReadonlySet<string>;
  toggleThread: (key: string, open?: boolean) => void;
  sheets: SheetKey[];
  openSheet: (k: SheetKey, push?: boolean) => void;
  closeSheet: () => void;
  backSheet: () => void;
  menu: MenuState | null;
  openMenu: (m: MenuState | null) => void;
  toast: (words: string) => void;
  viewing: Viewing | null;
  view: (v: Viewing | null) => void;
  /** The talk the sheet plays: a room, and a series' session's replay when one was picked; `start` plays it at once, `at` from that second. */
  playing: { webinar_id: string; session_id?: string; start?: boolean; at?: number } | null;
  playTalk: (webinarId: string, opts?: { sessionId?: string; start?: boolean; at?: number }) => void;
  /** The words on the host's video notes, on until the person turns them off. */
  captionsOn: boolean;
  toggleCaptions: () => void;

  // --- remembered on this device ---
  inCalendar: (key: string) => boolean;
  markCalendar: (key: string) => void;
  saved: (key: string) => boolean;
  markSaved: (key: string) => void;

  // --- the conversation ---
  replying: Replying | null;
  setReplying: (r: Replying | null) => void;
  replyTo: (e: Entry) => void;
  sendText: (body: string, chips: Chip[]) => void;
  sendVideo: (blob: Blob, live: LiveNoteUpload | null) => void;
  retry: (cid: string) => void;
  /** A video on its way: its preview, until the server's copy plays. */
  localMedia: (cid: string) => string | null;
  /** A video on its way that the person picked (not recorded): its name and size, for its tile. */
  localPicked: (cid: string) => { name: string; size: string } | null;
  /** The files a message on its way carries, until they are uploaded. */
  localFiles: (cid: string) => Chip[];
  love: (e: Entry) => void;
  isPinned: (e: Entry) => boolean;
  togglePin: (e: Entry) => void;
  unpin: (pin: WatchPin) => void;
  seePin: (pin: WatchPin) => void;
  goTo: (entryId: string) => void;
  answerQuestion: (questionId: string, webinarId: string, option: string) => void;
  changeAnswer: (a: WatchAnswer, value: string) => Promise<void>;
  noteTyping: (webinarId: string) => void;
  defaultRoom: string;

  // --- rooms ---
  leaveSeries: (webinarId: string) => Promise<void>;
  setPhoto: (file: File | null) => Promise<void>;
  notYou: () => void;
  refresh: () => void;
  /** Reads What's new and every place's counts again, as any place's wire asks. */
  refreshGuest: () => void;
}

export const PageContext = createContext<PageValue | null>(null);

export function usePage(): PageValue {
  const v = useContext(PageContext);
  if (!v) throw new Error("usePage outside their page");
  return v;
}
